export const DEBUG_ANALYTICS = import.meta.env.VITE_DEBUG_ANALYTICS === 'true';

export const TRACKER_IDS = {
  sp: {
    default: 'sp-0',
    legacy: 'sp-legacy',
  },
};

// Some paths are ignored because they use a new approach for logging page views
export const IGNORED_SNOWPLOW_PATHS = /^\/(profile|card\/embed)\/\d+($|\/|\?)/;

/**
 * Structured event categories
 * https://github.com/kluein/klue-snowplow#structured-events
 */
export const StructuredAnalyticsCategory = {
  profile: 'Profile',
  battlecard: 'Battlecard',
  search: 'Global Search',
  zoom: 'Content Zoom',
  curatorAddedLinks: 'Curator added links',
  // Report
  report: 'report',
} as const;

/**
 * Unstructured event categories (recommended)
 * https://github.com/kluein/klue-snowplow#unstructured-eventsentities
 *
 * - Requires a schema definition in the `klue-snowplow` repository
 * - Requires a schema URL to be defined per value in the `SNOWPLOW_SCHEMAS` object
 * - Requires an event definition, created in src/lib/analytics/snowplow/events
 *   - These defs prepare event payloads based on the params you provide to `logEvent()`
 */
export const AnalyticsCategory = {
  ...StructuredAnalyticsCategory,
  // Intel
  intelAction: 'intel_action',
  impression: 'impression',
  // Card
  cardInteraction: 'card_interaction',
  // Search
  searchAction: 'search_action',
  // Insights_action
  insightsAction: 'insights_action',
  // Ask Klue
  askKlueInteraction: 'ask_klue_interaction',
  // Chatbot Actions
  chatbotFollowupAction: 'chatbot_followup_action',
  chatbotCopyAction: 'chatbot_copy_action',
  // Session feedback prompt
  chatFeedbackPromptAction: 'chat_feedback_prompt_action',
  // Compete answer comparison
  competeAnswerComparisonAction: 'compete_answer_comparison_action',
  // Deal Tips
  dealTipFlag: 'deal_tip_flag',
  // Intel Digest 2.0 curation + delivery (ENG-3782)
  digestTakeAction: 'digest_take_action',
  digestStoryAdd: 'digest_story_add',
  digestAction: 'digest_action',
  // Integration Card Actions
  integrationCardAction: 'integration_card_action',
  // CRM Data Sync
  dataSyncConfigurationComplete: 'data_sync_configuration_complete',
  dataSyncAuthModified: 'data_sync_auth_modified',
  // Call Insights
  callDataSyncAuthModified: 'call_data_sync_auth_modified',
  callDataSyncConfigurationComplete: 'call_data_sync_configuration_complete',
  // Drive data Sync
  driveDataSyncAuthModified: 'drive_data_sync_auth_modified',
  driveDataSyncConfigurationComplete: 'drive_data_sync_configuration_complete',
  // Competitive Dashboard
  competitiveDashboardAction: 'competitive_dashboard_action',
  // Company Landing Page
  landingPageAction: 'landing_page_action',
  // Dashboard Tile Action
  dashboardTileAction: 'dashboard_tile_action',
  //Alerts Notification on Dashboard Action
  alertsNotificationAction: 'alerts_notification_action',
} as const;

export const AnalyticsAction = {
  view: 'view',
  tagClick: 'Tag Click',
  search: 'search',
  searchEmpty: 'search (no results)',
  tagFilter: 'Filter By Tag',
  tagSearch: 'Search by Tag',
  battlecardSearch: 'click_battlecard_search',
  //Intel
  intel: 'intel',
  visitSource: 'visit_source',
  visitLink: 'visit_link',
  addWhyItMatters: 'add_why_it_matters',
  editWhyItMatters: 'edit_why_it_matters',
  removeWhyItMatters: 'remove_why_it_matters',
  addToDigest: 'add_to_digest',
  removeFromDigest: 'remove_from_digest',
  sendToSlack: 'send_to_slack',
  publishToSlack: 'publish_to_slack',
  monitor: 'monitor',
  addImportant: 'add_important',
  addInteresting: 'add_interesting',
  removeImportant: 'remove_important',
  removeInteresting: 'remove_interesting',
  addTag: 'add_tag',
  removeTag: 'remove_tag',
  removeCard: 'remove_card',
  addBoard: 'add_board',
  removeBoard: 'remove_board',
  visitBoard: 'visit_board',
  publish: 'publish',
  unpublish: 'unpublish',
  archived: 'archived',
  deleted: 'deleted',
  unprocessed: 'unprocessed',
  working: 'working',
  skip: 'skip',
  unknown: 'unknown', // TODO: I don't see this action type in Slite docs. should we remove this and fix the place that is sending this instead?
  // card
  assignToBattlecards: 'assign_to_battlecards',
  cardPermissions: 'card_permissions',
  scheduleUpdate: 'schedule_update',
  positiveFeedback: 'positive_feedback',
  negativeFeedback: 'negative_feedback',
  removedFeedback: 'removed_feedback',
  expand: 'expand',
  viewSources: 'view_sources',
  edit: 'click_edit',
  copyEmbed: 'copy_embed',
  copyLink: 'copy_share',
  duplicateCard: 'duplicate_card',
  moveCard: 'move_card',
  link: 'link',
  table: 'table',
  boardCard: 'board_card',
  card: 'card',
  tag: 'tag_click',
  image: 'image',
  highlight: 'highlight',
  createAskKlueCard: 'create_ask_klue_card',
  viewAskKlueCard: 'view_ask_klue_card',
  // intel action
  bulkAction: 'bulk_action',
  // insights_action
  updateFilter: 'update_filter',
  updateInsightFilter: 'update_insight_filter',
  pinReview: 'pin_review',
  unpinReview: 'unpin_review',
  hideAspect: 'hide_aspect',
  unhideAspect: 'unhide_aspect',
  visitReviewSource: 'visit_source',
  generateSummary: 'generate_summary',
  sourceButtonClick: 'source_button_click',
  createInsightCard: 'create_insight_card',
  copyTopicSummary: 'copy_topic_summary',
  createTopicSummaryCard: 'create_topic_summary_card',
  // search
  generateResult: 'generate_result',
  generateResultFail: 'generate_result_fail',
  supportedRivalsResults: 'supported_rivals_result',
  // ask_klue
  ask: 'ask',
  askFollowup: 'ask_followup',
  askSuggest: 'ask_suggest',
  renderResponse: 'render_response',
  copyResult: 'copy_result',
  openModal: 'open_modal',
  closeModal: 'close_modal',
  renderAskKlue: 'render_ask_klue',
  renderAskSuggest: 'render_ask_suggest',
  focusTextInputBox: 'focus_text_input_box',
  yesAskKlueFeedback: 'yes_ask_klue_feedback',
  noAskKlueFeedback: 'no_ask_klue_feedback',
  skipAskKlueFeedback: 'skip_ask_klue_feedback',
  openAnswerSourceItem: 'open_answer_source_item',
  clickChatSessionItem: 'click_chat_session_item',
  // report
  downloadChart: 'download_chart',
  drillDown: 'drill_down',
  // integration
  clickInstall: 'click_install',
  clickUninstall: 'click_uninstall',
  clickConnect: 'click_connect',
  clickWaitlist: 'click_waitlist',
  clickContactSupport: 'click_contact_support',
  clickResumeInstall: 'click_resume_install',
  clickResolveDegraded: 'click_resolve_degraded',
  clickManageSSO: 'click_manage_sso',
  // crm data sync
  dataSyncConfigurationComplete: 'data_sync_configuration_complete',
  dataSyncAuthModified: 'data_sync_auth_modified',
  // drive data sync
  driveDataSyncConfigurationComplete: 'drive_data_sync_configuration_complete',
  driveDataSyncAuthModified: 'drive_data_sync_auth_modified',
  // research module
  shareFeedback: 'share_feedback',
  // call insights
  callDataSyncAuthModified: 'call_data_sync_auth_modified',
  callDataSyncConfigurationComplete: 'call_data_sync_configuration_complete',
  callDataSyncToggled: 'call_data_sync_toggled',
  // competitive dashboard
  competitiveDashboardAction: 'competitive_dashboard_action',
  competitiveDashboardClick: 'competitive_dashboard_click',
  competitiveDashboardView: 'view',
  // company landing page
  landingPageVisit: 'visit_page',
  landingPageInsightView: 'view_insight',
  landingPageWhatsNewView: 'view_whats_new',
  landingPageBattlecardClick: 'click_battlecard',
  landingPageWhatsNewClick: 'click_whats_new',
  landingPageShareLink: 'share_link',
  // dashboard tile action
  dashboardTileView: 'view_tile',
  dashboardTileClick: 'click_tile',
  // alert notification tile action
  alertTileView: 'view_tile',
  alertTileClick: 'click_tile',
  // chatbot followup action
  clickFollowupAction: 'click_followup_action',
  // chatbot copy action
  chatbotCopy: 'copy',
  // session feedback prompt; answers land in thread_feedback, not here
  chatFeedbackPromptShown: 'shown',
  chatFeedbackPromptSkipped: 'skipped',
  chatFeedbackPromptDismissed: 'dismissed',
  // compete answer comparison action
  competeAnswerComparisonDatasetViewed:
    'compete_answer_comparison_dataset_viewed',
  competeAnswerComparisonQuestionViewed:
    'compete_answer_comparison_question_viewed',
  competeAnswerComparisonVoteSubmitted:
    'compete_answer_comparison_vote_submitted',
  competeAnswerComparisonCompleted: 'compete_answer_comparison_completed',
  // deal tip flag
  dealTipSubmitFlag: 'submit_flag',
  // digest 2.0 takes (ENG-3782). One set for issue takes and story takes
  // alike — the artifact entity says which one was acted on, so the action
  // does not have to.
  digestTakeAdded: 'added',
  digestTakeEdited: 'edited',
  digestTakeApproved: 'approved',
  digestTakeRemoved: 'removed',
  // digest 2.0 actions that need nothing but their own name (ENG-3782)
  digestStoryRemoved: 'story_removed',
  digestTitleEdited: 'title_edited',
  digestSendNow: 'send_now',
  digestSkipped: 'skipped',
  // digest 2.0 story additions (ENG-3782). `digest_story_add` has no action
  // property of its own — `method` and `outcome` carry the meaning — so this
  // only satisfies the tracker's category/action pair and is never sent.
  digestStoryAdd: 'story_add',
} as const;

export const ChatbotCopyType = {
  richText: 'rich_text',
  markdown: 'markdown',
} as const;
